import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDocument } from "~/api/queries";
import { Avatar } from "~/foundation/components/Avatar";
import { Empty } from "~/foundation/components/Empty";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { formatDateTime, formatRelativeTime } from "~/foundation/utils/time";

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-divider p-4">
      <p className="text-small text-text-tertiary">{label}</p>
      <p className="mt-1 text-h3 font-semibold text-text">{value}</p>
    </div>
  );
}

export function DocumentInsightsScene({ documentId }: { documentId: string }) {
  const { t, i18n } = useTranslation();
  const { data: document, isLoading } = useDocument(documentId);
  useDocumentTitle(document ? `${t("Insights")} · ${document.title}` : t("Insights"));

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("Loading")} />
      </div>
    );
  }
  if (!document) {
    return <Empty message={t("This document could not be loaded")} />;
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 p-6">
      <div>
        <Link
          to="/documents/$documentId"
          params={{ documentId }}
          className="text-small text-text-tertiary hover:text-text"
        >
          ← {document.title}
        </Link>
        <Text as="h1" variant="h2" className="mt-1">
          {t("Insights")}
        </Text>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label={t("Views")} value={String(document.views)} />
        <Stat
          label={t("Last updated")}
          value={formatRelativeTime(document.updatedAt, i18n.language)}
        />
        <Stat label={t("Created")} value={formatDateTime(document.createdAt, i18n.language)} />
      </div>

      <div className="rounded-md border border-divider p-4">
        <Text variant="small" weight="medium" className="uppercase tracking-wide">
          {t("Created by")}
        </Text>
        <div className="mt-2 flex items-center gap-2">
          <Avatar name={document.createdBy.name} src={document.createdBy.avatarUrl} size={28} />
          <Text variant="body">{document.createdBy.name}</Text>
        </div>
      </div>
    </div>
  );
}
